// import React from 'react';
import { useTranslation } from 'react-i18next';
import { CellContext } from '@tanstack/react-table';
import { User } from '@/types/user.types';
import { formatDateAsYearMonthDayHourMinute } from '@/utils/formatters/dateFormatter';
import { formatTimezone, formatTimezoneShort } from '@/utils/formatters/timezoneFormatter';
import { UserStatusCell } from '../UserStatusCell';

// TODO: Remove this once we have a proper type for the object
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const UserNameCell = (info: CellContext<User, any>) => {
  const value = info.getValue();
  if (!value) return '';

  return String(value)
    .toLowerCase()
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

// TODO: Remove this once we have a proper type for the object
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const UserSurnameCell = (info: CellContext<User, any>) => {
  const value = info.getValue();
  if (!value) return '';

  return String(value)
    .toLowerCase()
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

// TODO: Remove this once we have a proper type for the object
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const UserUsernameCell = (info: CellContext<User, any>) => {
  return info.getValue();
};

// TODO: Remove this once we have a proper type for the object
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const UserRoleCell = (info: CellContext<User, any>) => {
  const { t } = useTranslation();
  return t(`pages.users.roles.${info.getValue().toLowerCase()}`);
};

// TODO: Remove this once we have a proper type for the object
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const UserTimezoneCell = (info: CellContext<User, any>) => {
  return formatTimezone(info.getValue());
};

// TODO: Remove this once we have a proper type for the object
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const UserLastLoginCell = (info: CellContext<User, any>) => {
  const sm = info.row.original.sessionManagement;
  const tz = sm?.lastLoginTz ?? 'UTC';
  return formatDateAsYearMonthDayHourMinute(sm?.lastLogin, tz);
};

// IANA guardada en el último login (legacy sin dato → UTC en UI)
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const UserLastLoginTzCell = (info: CellContext<User, any>) => {
  const tz = info.row.original.sessionManagement?.lastLoginTz ?? 'UTC';
  return <span className="text-xs text-gray-600">{formatTimezoneShort(tz)}</span>;
};

// TODO: Remove this once we have a proper type for the object
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const UserMetadataStatusCell = (info: CellContext<User, any>) => {
  const status = info.row.original.metadata?.status || 'active';
  return <UserStatusCell status={status} />;
};

// TODO: Remove this once we have a proper type for the object
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const UserSubscriptionCell = (info: CellContext<User, any>) => {
  const { t } = useTranslation();
  const subscription = info.getValue() || 'active';
  const isActive = subscription === 'active';

  return (
    <span
      className={`px-2 py-1 rounded-full text-xs font-medium ${
        isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
      }`}
    >
      {t(`pages.users.subscription.${subscription}`)}
    </span>
  );
};

// TODO: Remove this once we have a proper type for the object
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const UserSubscriptionExpiryCell = (info: CellContext<User, any>) => {
  const { t } = useTranslation();
  const user = info.row.original;
  const expiryDate = info.getValue();

  // If user is admin, show "Permanente" (has priority)
  if (user.role === 'Administrador') {
    return (
      <span className="px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
        {t('pages.users.subscription.permanent')}
      </span>
    );
  }

  // If subscription is inactive, show "Desactivado"
  if (user.metadata?.subscription === 'inactive') {
    return (
      <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
        Desactivado
      </span>
    );
  }

  // If no expiry date, show dash
  if (!expiryDate) return '-';

  const now = new Date();
  const expiry = new Date(expiryDate);
  const diffTime = expiry.getTime() - now.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  const diffYears = diffDays / 365;

  // If more than 10 years, show "Permanente" in green
  if (diffYears > 10) {
    return (
      <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
        {t('pages.users.subscription.permanent')}
      </span>
    );
  }

  let colorClass = '';
  if (diffDays < 0) {
    // Expired
    colorClass = 'bg-red-100 text-red-800';
  } else if (diffDays <= 14) {
    // Less than 2 weeks
    colorClass = 'bg-red-100 text-red-800';
  } else if (diffDays <= 30) {
    // Less than 1 month
    colorClass = 'bg-yellow-100 text-yellow-800';
  } else {
    // More than 1 month
    colorClass = 'bg-green-100 text-green-800';
  }

  // Format date as YYYY/MM/DD without time
  const formattedDate = expiry.toLocaleDateString('en-CA'); // This gives YYYY-MM-DD format
  const displayDate = formattedDate.replace(/-/g, '/'); // Convert to YYYY/MM/DD

  return (
    <span className={`px-2 py-1 rounded-full text-xs font-medium ${colorClass}`}>
      {displayDate}
    </span>
  );
};
